import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Lock, KeyRound } from 'lucide-react';
import { accesoAPI } from '../api/api';

// Página pública: el empleado crea su propia contraseña del portal desde el
// enlace de invitación (un solo uso, 7 días). Misma maqueta que EmpleadoLogin.
// Si el enlace no sirve, explica qué hacer según el motivo que da el servidor
// (`detail.motivo`): usado (ya tiene contraseña: a iniciar sesión), sustituido
// (hay un enlace más nuevo), caducado o no_valido. No hay recuperación de
// contraseña en el ERP: la pide a dirección (enlace nuevo desde su ficha).
const REQUISITO = 'Mínimo 8 caracteres, con al menos una letra y un número.';
const RUTA_LOGIN = '/empleado/login';
const PEDIR_NUEVO = 'Pide a dirección que te envíe uno nuevo desde tu ficha.';

const cumpleRequisito = (pwd) => pwd.length >= 8 && /[A-Za-zÀ-ÿ]/.test(pwd) && /\d/.test(pwd);

const MOTIVOS = ['usado', 'sustituido', 'caducado', 'no_valido'];

// Motivo por el que el enlace no sirve, o null si el error es de otra cosa.
// Acepta también el formato antiguo (detail en texto) por si el servidor no se ha actualizado.
const motivoDelError = (err) => {
  const status = err?.response?.status;
  const detalle = err?.response?.data?.detail;
  if (detalle && typeof detalle === 'object' && MOTIVOS.includes(detalle.motivo)) return detalle.motivo;
  const texto = typeof detalle === 'string' ? detalle : '';
  if (status === 410) return /caducado/i.test(texto) ? 'caducado' : 'usado';
  if (status === 400 && /enlace/i.test(texto)) return 'no_valido';
  return null;
};

const TEXTOS_ENLACE = {
  usado: {
    titulo: 'Ya tienes contraseña',
    texto: 'Este enlace ya se utilizó para crear tu contraseña. Entra con tu correo y tu contraseña.',
    olvido: true,
  },
  sustituido: {
    titulo: 'Este enlace ya no vale',
    texto: 'Se te ha enviado un enlace más reciente. Usa el del último correo de bienvenida o pide a dirección que te envíe uno nuevo desde tu ficha.',
  },
  caducado: {
    titulo: 'El enlace ha caducado',
    texto: PEDIR_NUEVO,
  },
  no_valido: {
    titulo: 'Este enlace no es válido',
    texto: PEDIR_NUEVO,
  },
};

const CrearContrasena = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // cargando | formulario | hecho | error (sin respuesta) | uno de MOTIVOS (el enlace no sirve)
  const [estado, setEstado] = useState('cargando');
  const [intento, setIntento] = useState(0);
  const [invitacion, setInvitacion] = useState(null);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelado = false;
    const validar = async () => {
      if (!token) {
        setEstado('no_valido');
        return;
      }
      setEstado('cargando');
      try {
        const res = await accesoAPI.validarInvitacion(token);
        if (cancelado) return;
        setInvitacion(res.data || {});
        setEstado('formulario');
      } catch (err) {
        if (cancelado) return;
        const status = err.response?.status;
        // Sin respuesta o error del servidor: se puede reintentar; cualquier otro rechazo es enlace no válido
        setEstado(motivoDelError(err) || (!status || status >= 500 ? 'error' : 'no_valido'));
      }
    };
    validar();
    return () => {
      cancelado = true;
    };
  }, [token, intento]);

  const reintentar = useCallback(() => setIntento((n) => n + 1), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!cumpleRequisito(password)) {
      setError(`La contraseña no cumple el requisito. ${REQUISITO}`);
      return;
    }
    if (password !== password2) {
      setError('Las dos contraseñas no coinciden');
      return;
    }
    setGuardando(true);
    try {
      const res = await accesoAPI.crearPassword(token, password);
      setInvitacion((prev) => ({ ...(prev || {}), email: res.data?.email || prev?.email }));
      setPassword('');
      setPassword2('');
      setEstado('hecho');
    } catch (err) {
      const motivo = motivoDelError(err);
      if (motivo) {
        setEstado(motivo);
      } else {
        const detalle = err.response?.data?.detail;
        setError(typeof detalle === 'string' ? detalle : 'No se ha podido crear la contraseña');
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 sm:p-8" data-testid="crear-contrasena">
        <div className="text-center mb-8">
          <div className="inline-block p-3 bg-duetto-primary rounded-full mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Portal del Empleado</h1>
          {(estado === 'cargando' || estado === 'formulario') && (
            <p className="text-gray-600 mt-2">Crea tu contraseña de acceso</p>
          )}
        </div>

        {estado === 'cargando' && (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-duetto-primary"></div>
          </div>
        )}

        {TEXTOS_ENLACE[estado] && (
          <div className="space-y-5 text-center" data-testid={`crear-contrasena-${estado}`}>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{TEXTOS_ENLACE[estado].titulo}</h2>
              <p className="text-gray-600 mt-2">{TEXTOS_ENLACE[estado].texto}</p>
            </div>
            <a
              href={RUTA_LOGIN}
              className="inline-block w-full bg-duetto-primary text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition"
              data-testid="crear-contrasena-iniciar-sesion"
            >
              Iniciar sesión
            </a>
            {TEXTOS_ENLACE[estado].olvido && (
              <div className="text-sm text-gray-600 border-t border-gray-100 pt-4">
                <p className="font-medium text-gray-700">He olvidado mi contraseña</p>
                <p className="mt-1">Pide a dirección que te envíe un enlace nuevo desde tu ficha.</p>
              </div>
            )}
          </div>
        )}

        {estado === 'error' && (
          <div className="space-y-4 text-center" data-testid="crear-contrasena-error">
            <p className="text-gray-600">
              No se ha podido comprobar el enlace. Revisa la conexión y vuelve a intentarlo.
            </p>
            <button
              type="button"
              onClick={reintentar}
              className="w-full bg-duetto-primary text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition"
            >
              Reintentar
            </button>
          </div>
        )}

        {estado === 'formulario' && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="crear-contrasena-form">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">Hola, {invitacion?.nombre || ''}</p>
              {invitacion?.email && (
                <p className="text-sm text-gray-600 flex items-center justify-center gap-1 mt-1">
                  <User className="w-4 h-4 text-gray-400" />
                  {invitacion.email}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  data-testid="crear-contrasena-password"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repite la contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  autoComplete="new-password"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  data-testid="crear-contrasena-password2"
                  required
                />
              </div>
            </div>

            <p className={`text-xs ${password && !cumpleRequisito(password) ? 'text-red-600' : 'text-gray-500'}`}>
              {REQUISITO}
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={guardando}
              className="w-full bg-duetto-primary text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition disabled:opacity-50 flex items-center justify-center space-x-2"
              data-testid="crear-contrasena-submit"
            >
              {guardando ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <span>Crear contraseña</span>
              )}
            </button>
          </form>
        )}

        {estado === 'hecho' && (
          <div className="space-y-4 text-center" data-testid="crear-contrasena-hecho">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
              Contraseña creada. Ya puedes entrar
              {invitacion?.email ? ` con ${invitacion.email}` : ''}.
            </div>
            <a
              href={RUTA_LOGIN}
              className="inline-block w-full bg-duetto-primary text-white py-3 rounded-lg font-medium hover:bg-opacity-90 transition"
            >
              Ir al portal del empleado
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrearContrasena;
